import { useEffect, useMemo, useState } from "react";
import { _ROUTES } from "../../../utils/_routes_constants";
import { DataTable } from "../../ui/DataTable";
import { useBudgetData } from "../../../hooks/useBudgetData";
import { generateMonthColumns } from "../../../utils/AgGridUtils/ColumnBuilders/generateMonthColumns";
import {
  formDataToRow,
  MONTH_MAPPING_KEY,
  FORECAST_MONTH_MAPPING_KEY,
} from "./formDataToRow";
import { currencyFormatter } from "../../../utils/AgGridUtils/ColumnBuilders/createColumn";
import { getBudgetVsCrValue } from "../../../utils/AgGridUtils/getBudgetVsCrValue";
import BasicModal from "../../helpers/BasicModal";
import MessageIcon from "@mui/icons-material/Message";
import MarkUnreadChatAltIcon from "@mui/icons-material/MarkUnreadChatAlt";
import { IconButton } from "@mui/material";
import { Comments } from "./Comments";

export const Table = ({
  rows,
  setRows,
  params,
  isDataLoading,
  onDataLoadingStart,
  onDataLoadingStop,
}) => {
  const [showModal, setShowModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [loadedData, setLoadedData] = useState([]);
  const { loadData } = useBudgetData({
    setRows,
    onDataLoadingStart,
    onDataLoadingStop,
    params,
    formDataToRow: (data) => formDataToRow(data),
  });

  useEffect(() => {
    const fetchData = async () => {
      const data = await loadData(_ROUTES.REAL_DATA);
      setLoadedData(data);
    };

    fetchData();
  }, [params.activity, params.site]);

  const updateRowComment = (accountParameterId, comment) => {
    setRows((prevRows) =>
      prevRows.map((row) => {
        if (row.account_parameter_id === accountParameterId) {
          return {
            ...row,
            comments: comment,
          };
        }
        return row;
      }),
    );
  };

  // Generate columns based on fiscal years
  const generateFiscalYearColumns = useMemo(() => {
    if (!loadedData || !loadedData.data || loadedData.data.length === 0) {
      return [];
    }

    // Create a map to store unique fiscal year information
    const fiscalYearMap = new Map();

    // Process the data to extract fiscal year information
    loadedData.data.forEach((yearData) => {
      const { fiscal_year, fiscal_year_type, fiscal_year_version } = yearData;
      const key = fiscal_year_version
        ? `${fiscal_year}|${fiscal_year_type}|${fiscal_year_version}`
        : `${fiscal_year}|${fiscal_year_type}`;

      fiscalYearMap.set(key, {
        year: fiscal_year,
        type: fiscal_year_type,
        version: fiscal_year_version,
      });
    });

    // Convert to array and sort by year
    const fiscalYearsArray = Array.from(fiscalYearMap.entries())
      .map(([key, value]) => ({
        key,
        ...value,
      }))
      .sort((a, b) => {
        // First sort by year
        const yearA = parseInt(a.year.split("-")[0]);
        const yearB = parseInt(b.year.split("-")[0]);
        if (yearA !== yearB) return yearA - yearB;

        // Then by type (R comes before F which comes before B)
        const typeOrder = { R: 0, F: 1, B: 2 };
        if (a.type !== b.type) return typeOrder[a.type] - typeOrder[b.type];

        // Then by version if present
        return (a.version || 0) - (b.version || 0);
      });

    const columns = fiscalYearsArray.map((fiscalYear) => {
      const { key, year, type, version } = fiscalYear;

      // Create a display name based on the fiscal year type and version
      let headerName;

      if (type === "B") {
        headerName = `Budget ${year}${version ? ` - ${version}` : ""}`;
      } else if (type === "F") {
        headerName = `Forecast ${year}${version ? ` - ${version}` : ""}`;
      } else {
        headerName = year;
      }

      return {
        headerName: headerName,
        field: `${MONTH_MAPPING_KEY}.${key}`,
        // Open by default the last 2 fiscal years
        openByDefault:
          fiscalYearsArray.indexOf(fiscalYear) >= fiscalYearsArray.length - 2,
        suppressHeaderMenuButton: true,
        type: "rightAligned",
        headerClass:
          type === "B"
            ? "fiscal-year-header-col-budget"
            : type === "F"
            ? "fiscal-year-header-col-forecast"
            : "fiscal-year-header-col",
        headerStyle: (params) => {
          const style = {
            color: "#000",
          };

          if (type === "B") {
            style.backgroundColor = "rgb(254, 248, 160)";
          } else if (type === "F") {
            style.backgroundColor = "rgb(145, 228, 251)";
          } else {
            const currentYear = new Date().getFullYear();
            const lastYear = parseInt(year.split("-")[1]);

            if (lastYear === currentYear) {
              style.backgroundColor = "rgb(174, 221, 148)";
            }
          }

          return style;
        },
        children: [
          ...generateMonthColumns([], {
            cellRenderer: (params) => {
              if (params.data?.is_separator) return null;
              if (params.data?.is_ton) {
                return currencyFormatter(`${params.value}`, "T", 0);
              } else if (params.data?.is_percent) {
                return `${params.value}%`;
              } else if (params.data?.is_decimal) {
                return currencyFormatter(`${params.value}`, "", 2);
              }

              return currencyFormatter(params.value, "", 0);
            },
            type: "rightAligned",
            cellStyle: (params) => {
              // Skip styling if params or data is missing
              if (!params || !params.data) {
                return null;
              }

              // Extract the month number from the column ID
              const columnId = params.column.getColId();
              const monthMatch = columnId.match(/\.(\d+)$/);

              if (!monthMatch) {
                return null;
              }

              const month = parseInt(monthMatch[1], 10);

              // Check if this specific month is marked as a forecast in the data
              const forecastMonths =
                params.data[FORECAST_MONTH_MAPPING_KEY]?.[key];

              if (forecastMonths && forecastMonths[month]) {
                // Highlight forecast data in red
                return { color: "#FF0000", fontStyle: "italic" };
              }

              return null;
            },
            columnGroupShow: "open",
            editable: false,
            _custom: {
              monthKeyPrefix: `${MONTH_MAPPING_KEY}.${key}`,
            },
          }),
          {
            headerName: "Total",
            headerClass: "total-header-col",
            field: `total_${key}`,
            editable: false,
            cellDataType: "number",
            type: "rightAligned",
            suppressHeaderMenuButton: true,
            valueFormatter: (params) => {
              if (params.data?.is_separator) return null;
              if (!params || !params.data || !params.data[MONTH_MAPPING_KEY]) {
                return "";
              }

              const fiscalYearData = params.data[MONTH_MAPPING_KEY][key];

              if (!fiscalYearData) {
                return "";
              }

              const total = Object.values(fiscalYearData).reduce(
                (acc, curr) => {
                  return acc + curr;
                },
                0,
              );

              return currencyFormatter(
                total,
                params.data?.is_ton ? "T" : params.data?.is_percent ? "%" : "",
                params.data?.is_ton ? 0 : params.data?.is_percent ? 2 : (params.data?.is_decimal ? 2 : 0),
              );
            },
          },
        ],
      };
    });

    return columns;
  }, [loadedData]);

  // Generate the header name
  const headerName = useMemo(() => {
    if (!loadedData || !loadedData.activity_code) {
      return "Loading...";
    }

    const siteCodes = loadedData.site_codes
      ? loadedData.site_codes.join(", ")
      : "";

    return `${loadedData.activity_code} - ${siteCodes}`;
  }, [loadedData]);

  const handleAutoSizeColumns = (event) => {
    event.api.autoSizeAllColumns();
    event.api.sizeColumnsToFit();
  };

  return (
    <>
      {/* Modal  */}
      <BasicModal
        showModal={showModal}
        setShowModal={setShowModal}
        title={"Comments"}
        content={
          <Comments
            params={params}
            rowData={selectedRow}
            onCommentUpdate={(comment) => {
              updateRowComment(selectedRow.account_parameter_id, comment);
              setShowModal(false);
            }}
          />
        }
        icon={<MessageIcon />}
        width={"500"}
      />
      <DataTable
        isDataLoading={isDataLoading}
        rows={rows}
        columnsIncludeMonths={false}
        columns={[
          {
            headerName: "Note",
            field: "comments",
            flex: 1,
            pinned: "right",
            cellRenderer: (params) => {
              if (params.data?.is_separator_for) return "";
              return (
                <div>
                  <IconButton
                    aria-label="comment"
                    size="small"
                    onClick={() => {
                      setSelectedRow(params.data);
                      setShowModal(true);
                    }}
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    {params.data?.comments ? (
                      <MarkUnreadChatAltIcon color="lightDanger" />
                    ) : (
                      <MessageIcon color="primary" />
                    )}
                  </IconButton>
                </div>
              );
            },
          },
          {
            headerName: headerName,
            field: "name",
            flex: 2,
            suppressHeaderMenuButton: true,
            // For rows
            wrapText: true,
            autoHeight: true,
            // For header
            wrapHeaderText: true,
            autoHeaderHeight: true,
            pinned: "left",
          },
          ...generateFiscalYearColumns,
          ...(rows && rows.length > 0
            ? [
                {
                  headerName: "Budget vs Current Year",
                  field: "budget_vs_cr",
                  editable: false,
                  openByDefault: true,
                  suppressHeaderMenuButton: true,
                  headerStyle: {
                    color: "#000",
                    backgroundColor: "rgb(246, 162, 191)",
                  },
                  children: [
                    {
                      headerName: "Rs",
                      field: "budget_vs_cr_rs",
                      editable: false,
                      cellDataType: "number",
                      suppressHeaderMenuButton: true,
                      valueFormatter: (params) => {
                        if (params.data?.is_separator) return "";
                        return currencyFormatter(getBudgetVsCrValue(params),"",0);
                      },
                    },
                    {
                      headerName: "%",
                      field: "budget_vs_cr_percent",
                      editable: false,
                      cellDataType: "number",
                      suppressHeaderMenuButton: true,
                      
                      valueFormatter: (params) => {
                        if (params.data?.is_separator) return "";
                        return currencyFormatter(getBudgetVsCrValue(params, true));
                      },
                    },
                  ],
                },
              ]
            : []),
        ]}
        agGridProps={{
          defaultColDef: {
            suppressSizeToFit: true,
          },
          autoSizeStrategy: {
            type: "fitCellContents",
          },
          onGridReady: handleAutoSizeColumns,
          onColumnGroupOpened: handleAutoSizeColumns,
          onGridSizeChanged: handleAutoSizeColumns,
          groupDisplayType: "groupRows",
          getRowStyle: (params) => {
            const style = {};
            if (params.node?.data?.is_children === true) {
              style.color = "grey";
              style.fontStyle = "italic";
              style.fontSize = "0.8em";
              style.textAlign = "right";
            } else {
              style.color = "black";
              style.fontStyle = "normal";
              style.fontSize = "1em";
              style.textAlign = "right";
            }
            if (params.node?.data?.color_background) {
              style.background = params.node.data.color_background;
            }

            if (params.node?.data?.color_text) {
              style.color = params.node.data.color_text;
            }

            if (params.node?.data?.is_children === true) {
              style.color = "grey";
              style.fontStyle = "italic";
              style.fontSize = "0.8em";
              style.textAlign = "right";
            }

            return style;
          },
        }}
      />
    </>
  );
};
